'use client';
import React, { startTransition } from 'react';
import { Collapse, ErrorBoundary, Icon } from '@benzinga/core-ui';
import { Search } from '@benzinga/search-ui';
import styles from './HeaderMobile.module.scss';
import { QuotesList } from './QuotesList';
import { NavigationHeaderProps } from '../../entities';
import { Menu, MenuItemLink } from './Menu';
import { MenuInterface, MenuItem } from './interfaces';
import { ButtonBzPro } from '../BenzingaProButton';
import { DefaultQuotesList } from './_mockMenu';
import { GlobalMenu } from '../GlobalMenu';
import { AccountMenu } from '../AccountMenu';
import { queryFreestarStickyFooterElement } from '@benzinga/ads-utils';
import { appEnvironment, appName } from '@benzinga/utils';
import classnames from 'classnames';
import {
  useBodyLock,
  useEffectDidMount,
  useEventListener,
  useHydrate,
  useLocalStorage,
  useResizeObserver,
} from '@benzinga/hooks';
import i18n from '@benzinga/translate';
import { useTranslation } from 'react-i18next';
import { useIsUserLoggedIn, useUser } from '@benzinga/user-context';
import { getNavMenuItemHref, NavMenuItemHrefInput } from '../../premiumServiceLinks';
import { usePremiumServiceAccess } from '../../hooks/usePremiumServiceAccess';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import { faBars } from '@fortawesome/pro-light-svg-icons/faBars';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import type { BreakingNews } from '@benzinga/basic-news-manager';
import { BreakingNewsBanner } from '../BreakingNewsBanner';
import { EmailVerificationBanner } from '../EmailVerificationBanner';
import { useRevealOnFirstScroll } from '../../hooks/useRevealOnFirstScroll';
import { NavigationLogo } from './NavigationLogo';
import { measureStickyNavOffset, setStickyNavOffset } from '../../utils/stickyNavOffset';
import { useRouter } from 'next/navigation';
import { OptimizelyTopBanner } from './RotatingBannerFallback';
import { AdHeaderBanner } from './AdHeaderBanner';

export interface HeaderMobileProps {
  breakingNews?: BreakingNews[];
  isActiveHeader?: boolean;
  AboveHeaderBlock?: React.ReactNode;
}

type IExtendedMenu = Partial<MenuInterface> & { links?: MenuItem[] };
type PathInterface = [undefined, ...IExtendedMenu[]];

const constants = {
  MAIN_BLOCK_HEIGHT: 49,
  QUOTE_BAR_HEIGHT: 52,
  ROTATING_BANNER_HEIGHT: 80,
  SEARCH_BAR_HEIGHT: 61,
};

export const HeaderMobile = ({
  AboveHeaderBlock,
  breakingNews,
  expandedHeaderDisplayAd,
  featuredMenu,
  hideMenuBar,
  hideQuoteBar,
  hideSearchBar,
  initialTrendingTickers,
  initialTrendingTopics,
  isActiveHeader,
  isMainDrawerVisible,
  logoVariant,
  marketTickers,
  menus,
  quotes,
  shouldRenderHeaderDisplayAd,
  showHeaderDisplayAd,
  showRotatingBanner,
}: NavigationHeaderProps & HeaderMobileProps) => {
  const [isMenuVisible, setIsMenuVisible] = React.useState(false);
  const [isFullScreenSearch, setIsFullScreenSearch] = React.useState(false);
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const lastScrollPositionRef = React.useRef(0);
  const isCollapsedRef = React.useRef(false);
  const [hideRotatingBanner] = useLocalStorage('hideRotatingBanner', !showRotatingBanner);
  const hideRotatingBannerHydrated = useHydrate(hideRotatingBanner, showRotatingBanner === false ? true : false);
  const [extendedMenu, setExtendedMenu] = React.useState<IExtendedMenu | undefined>();
  const [path, setPath] = React.useState<PathInterface>([undefined]);

  const adHeaderBannerRef = React.useRef<HTMLDivElement>(null);
  const mobileBannerStackRef = React.useRef<HTMLDivElement | null>(null);

  const user = useUser();
  const showEmailVerificationBanner = Boolean(user?.email && user.emailVerified === false);
  const [breakingNewsVisible, setBreakingNewsVisible] = React.useState(false);
  const emailStripVisible = useRevealOnFirstScroll(showEmailVerificationBanner);

  React.useEffect(() => {
    if (!breakingNews?.length) {
      setBreakingNewsVisible(false);
    }
  }, [breakingNews?.length]);

  React.useEffect(() => {
    if (window?.scrollY > 0) {
      if (!breakingNewsVisible && breakingNews) {
        setBreakingNewsVisible(true);
      }
    }
  }, [breakingNews, breakingNewsVisible]);

  useEffectDidMount(() => {
    window?.addEventListener(
      'scroll',
      () => {
        if (!breakingNewsVisible && breakingNews) {
          setBreakingNewsVisible(true);
        }
      },
      { once: true },
    );
  });

  const headerStripRowCount =
    (showEmailVerificationBanner && emailStripVisible ? 1 : 0) +
    (breakingNews && breakingNews.length > 0 && breakingNewsVisible ? 1 : 0);

  const showSearchBar = !hideQuoteBar && !hideSearchBar;
  const showMobileBannerStrip = !hideQuoteBar && headerStripRowCount > 0;

  const isLoggedIn = useHydrate(useIsUserLoggedIn(), false);
  const premiumServiceAccess = usePremiumServiceAccess(isLoggedIn);
  const getLinkHref = React.useCallback(
    (item: NavMenuItemHrefInput) => getNavMenuItemHref(item, isLoggedIn, premiumServiceAccess),
    [isLoggedIn, premiumServiceAccess],
  );

  const router = useRouter();

  const [heights, setHeights] = React.useState<{
    bannerHeight: number;
    mainBlockHeight: number;
    quoteBarHeight: number;
    searchBarHeight: number;
    headerDisplayAdHeight: number;
    rotatingBannerHeight: number;
  }>({
    bannerHeight: 0,
    headerDisplayAdHeight: 0,
    mainBlockHeight: constants.MAIN_BLOCK_HEIGHT,
    quoteBarHeight: constants.QUOTE_BAR_HEIGHT,
    rotatingBannerHeight: constants.ROTATING_BANNER_HEIGHT,
    searchBarHeight: constants.SEARCH_BAR_HEIGHT,
  });

  const {
    bannerHeight,
    headerDisplayAdHeight,
    mainBlockHeight,
    quoteBarHeight,
    rotatingBannerHeight,
    searchBarHeight,
  } = heights;

  const showRotatingBannerAbove = !hideRotatingBannerHydrated && showRotatingBanner;

  const headerStackHeight = hideQuoteBar
    ? mainBlockHeight +
      (showRotatingBannerAbove ? constants.ROTATING_BANNER_HEIGHT : 0) +
      (showHeaderDisplayAd ? headerDisplayAdHeight : 0)
    : mainBlockHeight +
      quoteBarHeight +
      searchBarHeight +
      bannerHeight +
      (hideRotatingBannerHydrated ? 0 : rotatingBannerHeight) +
      (showHeaderDisplayAd ? headerDisplayAdHeight : 0);

  React.useEffect(() => {
    if (!isActiveHeader) {
      return;
    }
    const mainBlockHeight =
      Number(document.getElementById('mobile-main-block')?.clientHeight) || constants.MAIN_BLOCK_HEIGHT;
    const quoteBarHeight = !hideQuoteBar ? Number(document.getElementById('mobile-quote-bar')?.clientHeight) : 0;
    const searchBarHeight = showSearchBar ? Number(document.getElementById('mobile-search-bar')?.clientHeight) : 0;

    const measuredAdHeight = adHeaderBannerRef.current?.clientHeight;
    const headerDisplayAdHeight = measuredAdHeight == null ? 0 : measuredAdHeight;

    startTransition(() => {
      setHeights(prev => ({
        ...prev,
        headerDisplayAdHeight,
        mainBlockHeight,
        quoteBarHeight,
        rotatingBannerHeight: constants.ROTATING_BANNER_HEIGHT,
        searchBarHeight,
      }));
    });
  }, [breakingNews, hideQuoteBar, isActiveHeader, showSearchBar]);

  React.useLayoutEffect(() => {
    if (!showMobileBannerStrip) {
      setHeights(prev => (prev.bannerHeight === 0 ? prev : { ...prev, bannerHeight: 0 }));
      return;
    }

    const bannerStackElement = mobileBannerStackRef.current;
    if (!bannerStackElement) {
      return;
    }

    const syncBannerStackHeight = () => {
      const measuredBannerHeight = Math.round(bannerStackElement.getBoundingClientRect().height);
      setHeights(previousHeights =>
        previousHeights.bannerHeight === measuredBannerHeight
          ? previousHeights
          : { ...previousHeights, bannerHeight: measuredBannerHeight },
      );
    };

    syncBannerStackHeight();

    const resizeObserver = new ResizeObserver(syncBannerStackHeight);
    resizeObserver.observe(bannerStackElement);
    return () => resizeObserver.disconnect();
  }, [
    showMobileBannerStrip,
    showEmailVerificationBanner,
    emailStripVisible,
    breakingNewsVisible,
    breakingNews,
    user?.email,
  ]);

  React.useEffect(() => {
    if (hideRotatingBanner) {
      startTransition(() => {
        setHeights(prevState => {
          return {
            ...prevState,
            streamlineBannerHeight: 0,
          };
        });
      });
    }
  }, [hideRotatingBanner]);

  React.useEffect(() => {
    isCollapsedRef.current = isCollapsed;
  }, [isCollapsed]);

  useBodyLock(isMenuVisible || isFullScreenSearch);

  useEventListener('scroll', () => {
    if (!hideQuoteBar) {
      const currentScrollPosition = window.scrollY;
      const isScrollingUp = lastScrollPositionRef.current > currentScrollPosition;

      if ((isCollapsedRef.current && currentScrollPosition <= headerStackHeight) || isScrollingUp) {
        if (isCollapsedRef.current) {
          isCollapsedRef.current = false;
          setIsCollapsed(false);
        }
      } else if (!isCollapsedRef.current && currentScrollPosition > headerStackHeight && !isScrollingUp) {
        isCollapsedRef.current = true;
        setIsCollapsed(true);
      }

      lastScrollPositionRef.current = currentScrollPosition;
    }
  });

  const handleLogout = React.useCallback(() => {
    let next = window.location.href;
    if (next.includes('/account')) {
      next = window.location.origin;
    }
    next = encodeURIComponent(next);

    const url = new URL(`${window.location.origin}/logout?next=${next}`);
    router.push(url.toString());
  }, [router]);

  const handleToggleMenuVisible = () => {
    setIsMenuVisible(!isMenuVisible);
    const footerAd = queryFreestarStickyFooterElement();
    if (footerAd) {
      footerAd.style.display = isMenuVisible ? 'flex' : 'none';
    }
  };

  const handleToggleFullScreenSearch = (value?: boolean) => {
    const newValue = value ? value : !isFullScreenSearch;
    setIsFullScreenSearch(newValue);
  };

  const primaryMenu = menus?.primary ?? [];
  const secondaryMenu = menus?.secondary.filter(menuItem => menuItem?.deviceType !== 'desktop') ?? [];

  const newSecondaryMenu = secondaryMenu.filter(item => item);
  const combinedMenu = [...primaryMenu, ...newSecondaryMenu];

  const handleClickMenuItem = (menu: MenuInterface | IExtendedMenu) => {
    if (menu.subnav) {
      setExtendedMenu(menu);
      const newPath: PathInterface = [...path];
      newPath.push(menu);
      setPath(newPath);
    } else if (menu.groups) {
      setExtendedMenu(menu as IExtendedMenu);
      const newPath: PathInterface = [...path];
      newPath.push(menu as IExtendedMenu);
      setPath(newPath);
    }
  };

  const handleBackButtonClick = () => {
    if (Array.isArray(path)) {
      const currentIndex = path.findIndex(pathObject => pathObject === extendedMenu);
      const previousPath = path[currentIndex - 1];
      setExtendedMenu(previousPath);
      if (typeof previousPath === 'undefined') {
        setExtendedMenu(undefined);
        setPath([undefined]);
      }
    }
  };

  // const handleDismissAdBanner = () => {
  //   setHideRotatingBanner(true);
  // };

  const fullHeaderCollapseDistance =
    mainBlockHeight +
    quoteBarHeight +
    (hideRotatingBannerHydrated ? 0 : rotatingBannerHeight) +
    (showHeaderDisplayAd ? headerDisplayAdHeight : 0);

  const mobileNavHeaderRef = React.useRef<HTMLDivElement | null>(null);

  const syncStickyNavOffset = React.useCallback(() => {
    const headerElement = mobileNavHeaderRef.current;
    if (!headerElement || headerElement.getClientRects().length === 0) return;
    const measuredOffsetPx = measureStickyNavOffset(headerElement);
    if (measuredOffsetPx <= 0) return;
    setStickyNavOffset(measuredOffsetPx);
  }, []);

  useResizeObserver(mobileNavHeaderRef, syncStickyNavOffset);

  React.useLayoutEffect(() => {
    syncStickyNavOffset();
    const headerElement = mobileNavHeaderRef.current;
    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== headerElement) return;
      syncStickyNavOffset();
    };
    headerElement?.addEventListener('transitionend', onTransitionEnd);
    return () => headerElement?.removeEventListener('transitionend', onTransitionEnd);
  }, [syncStickyNavOffset, isCollapsed, headerStackHeight]);

  return (
    <div
      className={styles.mobileHeaderContainer}
      style={
        {
          '--container-margin': `calc(var(--header-offset, 0px) + env(safe-area-inset-top, 0px))`,
          '--header-offset': `${headerStackHeight}px`,
          ...(showHeaderDisplayAd && headerDisplayAdHeight > 50
            ? { '--bz-header-ad-measured': `${headerDisplayAdHeight}px` }
            : {}),
        } as React.CSSProperties
      }
    >
      <div
        className={classnames(styles.mobileHeaderWrapper, 'z-40', {
          fullscreen: isFullScreenSearch,
          [styles.collapsed]: isCollapsed,
        })}
        id={isActiveHeader ? 'navigation-header' : undefined}
        ref={mobileNavHeaderRef}
        style={
          {
            '--search-fixed-top': `${headerStackHeight}px`,
            '--wrapper-top': isCollapsed ? `-${fullHeaderCollapseDistance}px` : '0',
          } as React.CSSProperties
        }
      >
        {showRotatingBanner && <OptimizelyTopBanner />}
        {showHeaderDisplayAd ? (
          <AdHeaderBanner
            expandedHeaderDisplayAd={expandedHeaderDisplayAd}
            headerType="mobile"
            loadAd={shouldRenderHeaderDisplayAd}
            ref={adHeaderBannerRef}
          />
        ) : null}
        <div className="left-0 right-0 top-0 z-40" id="mobile-main-block">
          {AboveHeaderBlock && (
            <div
              className="bg-white relative"
              style={{
                zIndex: 50,
              }}
            >
              {AboveHeaderBlock}
            </div>
          )}
          <div
            className="mobile-header flex items-center justify-between relative"
            style={{
              transition: 'all linear 0.3s',
              zIndex: 20,
            }}
          >
            {!hideMenuBar && isMainDrawerVisible !== false && (
              <div className="menu-icon-wrapper" onClick={handleToggleMenuVisible}>
                {isMenuVisible ? <Icon icon={faXmark} /> : <Icon icon={faBars} />}
              </div>
            )}
            <ErrorBoundary name="benzinga-logo-wrapper">
              <NavigationLogo deviceType="mobile" logoVariant={logoVariant} />
            </ErrorBoundary>
            <div className="flex h-12 items-center text-blue-300">
              <GlobalMenu />
              {!appEnvironment().isApp(appName.money) && (
                <AccountMenu deviceType="mobile" isLoggedIn={isLoggedIn} onLogout={handleLogout} />
              )}
            </div>
          </div>
        </div>
        {isMenuVisible ? (
          <div
            className={classnames(styles.mobileMenuWrapper, 'mobile-menu-wrapper')}
            style={{ '--menu-offset': `${headerStackHeight}px` } as React.CSSProperties}
          >
            <div className={styles.menuWrapper}>
              {extendedMenu ? (
                <div className="more-items-container">
                  <button className="back-button-container" onClick={handleBackButtonClick}>
                    <Icon className="chevron-left-icon" icon={faChevronLeft} />
                    <span>{extendedMenu.label}</span>
                  </button>
                  {extendedMenu?.label && (extendedMenu?.href || extendedMenu.logged_in_href) && (
                    <MenuItemLink
                      className="menu-item-link 2"
                      href={getLinkHref(extendedMenu)}
                      key={extendedMenu.label}
                    >
                      {extendedMenu.label}
                    </MenuItemLink>
                  )}
                  {Array.isArray(extendedMenu?.subnav?.links) &&
                    extendedMenu?.subnav?.links.map(link =>
                      link?.subnav?.links && Array.isArray(link?.subnav?.links) ? (
                        <div className="menu-item-group" key={link.label || link.href}>
                          <Collapse defaultCollapsed={true} header={link.label}>
                            <>
                              <MenuItemLink className="menu-item-link" href={getLinkHref(link)} key={link.label}>
                                {link.label}
                              </MenuItemLink>
                              {link?.subnav?.links.map(Sublink => (
                                <MenuItemLink
                                  className="menu-item-link"
                                  href={getLinkHref(Sublink)}
                                  key={Sublink.label}
                                >
                                  {Sublink.label}
                                </MenuItemLink>
                              ))}
                            </>
                          </Collapse>
                        </div>
                      ) : (
                        <MenuItemLink className="menu-item-link 3" href={getLinkHref(link)} key={link.label}>
                          {link.label}
                        </MenuItemLink>
                      ),
                    )}
                  {Array.isArray(extendedMenu?.groups) &&
                    extendedMenu.groups.map(group => (
                      <div className="menu-item-group" key={group.label}>
                        <Collapse defaultCollapsed={true} header={group.label}>
                          {group.links.map(link => (
                            <MenuItemLink className="menu-item-link 4" href={getLinkHref(link)} key={link.label}>
                              {link.label}
                            </MenuItemLink>
                          ))}
                        </Collapse>
                      </div>
                    ))}
                  {extendedMenu.label === 'Tools' && (
                    <ButtonBzPro
                      label="Free Benzinga Pro Trial"
                      url="https://pro.benzinga.com/register/?utm_source=tools-bz-pro-free-trial"
                      variant="trial"
                    />
                  )}
                </div>
              ) : (
                combinedMenu.map((menu: MenuInterface, mainMenuKey: number) => {
                  if (menu.subnav || menu.groups) {
                    return (
                      <MenuItemLink
                        as="div"
                        className="menu-item-link 5"
                        key={mainMenuKey}
                        onClick={() => handleClickMenuItem(menu)}
                      >
                        {menu.label}
                        <Icon className="chevron-right-icon" icon={faChevronRight} />
                      </MenuItemLink>
                    );
                  } else {
                    return <Menu key={mainMenuKey} menu={menu} />;
                  }
                })
              )}
              <FeaturedMenuItems items={featuredMenu} />
            </div>
            <MenuItemLink
              className="premium-button"
              href="https://www.benzinga.com/go/insider-report?utm_source=research-redirect"
              target="_blank"
            >
              <div>Research Services</div>
            </MenuItemLink>
          </div>
        ) : null}
        {!hideQuoteBar && !isFullScreenSearch ? (
          <div
            className="mobile-quote-bar overflow-x-scroll"
            id="mobile-quote-bar"
            style={{
              marginTop: '-.5px',
            }}
          >
            <QuotesList quotes={quotes} tickers={marketTickers ?? DefaultQuotesList} />
          </div>
        ) : null}
        {showSearchBar ? (
          <div
            className={classnames('search-wrapper transition', {
              fullscreen: isFullScreenSearch,
            })}
            id="mobile-search-bar"
            style={{
              marginTop: '-.5px',
              transition: 'all linear 0.3s',
            }}
          >
            <Search
              initialTrendingTickers={initialTrendingTickers}
              initialTrendingTopics={initialTrendingTopics}
              isFullScreenSearch={isFullScreenSearch}
              onToggle={handleToggleFullScreenSearch}
              placeholder="Search Tickers, Companies or News..."
              showTrending
            />
          </div>
        ) : null}
        {showMobileBannerStrip ? (
          <ErrorBoundary name="breaking-news-banner">
            <div
              className="mobile-header-banner-stack flex flex-col w-full"
              id="mobile-header-banner-stack"
              ref={mobileBannerStackRef}
            >
              {showEmailVerificationBanner && user ? <EmailVerificationBanner email={user.email} /> : null}
              {breakingNews && breakingNews?.length > 0 ? <BreakingNewsBanner data={breakingNews} /> : null}
            </div>
          </ErrorBoundary>
        ) : null}
      </div>
    </div>
  );
};

const FeaturedMenuItems: React.FC<{ items?: MenuItem[] }> = ({ items }) => {
  const { t } = useTranslation('common', { i18n });
  const isLoggedIn = useHydrate(useIsUserLoggedIn(), false);

  if (!items) return null;

  return (
    <ul className="mt-auto">
      {items?.map((item, key) => {
        return (
          <li className={styles.subNavigationItem} key={key}>
            <a
              className={styles.subNavigationLink}
              href={isLoggedIn && item.logged_in_href ? item.logged_in_href : item.href}
            >
              {t(item.title)}
            </a>
          </li>
        );
      })}
      <div className="bz-pro-logo border-blue-800 border-t-2 flex flex-shrink-0 text-white w-full">
        <ButtonBzPro
          label="Get Benzinga Pro"
          url="https://www.benzinga.com/pro/register?adType=homepagebluebutton&utm_source=homepage-blue-button&utm_campaign=14daytrial"
          variant="with-arrow"
        />
      </div>
    </ul>
  );
};
